import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  CalendarDaysIcon,
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface User {
  id: string;
  email: string;
  username: string;
  name: string;
  role: 'ADMIN' | 'RESIDENT';
  hoaId: string;
  contactNumber?: string;
  rentAmount?: number;
  createdAt: string;
}

interface CreateUserForm {
  email: string;
  username: string;
  name: string;
  role: 'ADMIN' | 'RESIDENT';
}

interface EditUserForm {
  name: string;
  email: string;
  username: string;
  contactNumber: string;
}

interface RentSchedule {
  id: string;
  userId: string;
  amount: number;
  frequency: string;
  startDate: string;
  endDate?: string | null;
  duration?: number | null;
  description: string;
  isActive: boolean;
  nextRunDate: string;
  createdAt: string;
  user?: { id: string; name: string; email: string };
}

interface RentScheduleForm {
  amount: string;
  frequency: string;
  startDate: string;
  duration: string;
  description: string;
}

const FREQUENCIES = [
  { value: 'WEEKLY', label: 'Weekly', abbr: '/wk' },
  { value: 'BI_WEEKLY', label: 'Bi-Weekly (every 2 weeks)', abbr: '/2wk' },
  { value: 'MONTHLY', label: 'Monthly', abbr: '/mo' },
  { value: 'QUARTERLY', label: 'Quarterly (every 3 months)', abbr: '/qtr' },
  { value: 'YEARLY', label: 'Yearly / Annually', abbr: '/yr' },
];

const freqAbbr = (f: string) => FREQUENCIES.find(x => x.value === f)?.abbr ?? '/mo';
const freqLabel = (f: string) => FREQUENCIES.find(x => x.value === f)?.label ?? f;

function calcEndDate(startDate: string, frequency: string, duration: number): Date | null {
  if (!startDate || !frequency || !duration || duration <= 0) return null;
  const end = new Date(startDate);
  switch (frequency) {
    case 'WEEKLY':    end.setDate(end.getDate() + duration * 7);       break;
    case 'BI_WEEKLY': end.setDate(end.getDate() + duration * 14);      break;
    case 'MONTHLY':   end.setMonth(end.getMonth() + duration);         break;
    case 'QUARTERLY': end.setMonth(end.getMonth() + duration * 3);     break;
    case 'YEARLY':    end.setFullYear(end.getFullYear() + duration);    break;
  }
  return end;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(n?: number | null): string {
  if (n === null || n === undefined) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
}

const emptyForm: RentScheduleForm = {
  amount: '',
  frequency: 'MONTHLY',
  startDate: new Date().toISOString().slice(0, 10),
  duration: '',
  description: ''
};

const Users: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  // Edit user modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState<EditUserForm>({ name: '', email: '', username: '', contactNumber: '' });

  // Add user modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [sendingResetFor, setSendingResetFor] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateUserForm>({
    email: '', username: '', name: '', role: 'RESIDENT'
  });

  // Rent schedule modal
  const [showRentModal, setShowRentModal] = useState(false);
  const [rentModalUser, setRentModalUser] = useState<User | null>(null);
  const [schedules, setSchedules] = useState<RentSchedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<RentScheduleForm>(emptyForm);
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const headers = useCallback(() => {
    const g = localStorage.getItem('selectedPropertyGroup');
    return g ? { 'x-selected-group': g } : {};
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!user?.token) return;
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    if (!selectedGroupId) {
      setMessage('Please select a property group first');
      setMessageType('error');
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/api/users', { headers: { 'x-selected-group': selectedGroupId } });
      const all = response.data as any[];
      setUsers(all.filter(u => u.role === 'ADMIN' || u.role === 'RESIDENT'));
    } catch {
      setMessage('Error loading users');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    if (user?.role === 'ADMIN') fetchUsers();
    else setLoading(false);
  }, [fetchUsers, user?.role]);

  // ── User CRUD ────────────────────────────────────────────────────────────────

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Delete ${userName}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/users/${userId}`);
      setMessage('User deleted');
      setMessageType('success');
      fetchUsers();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Error deleting user');
      setMessageType('error');
    }
  };

  const handleSendResetEmail = async (userId: string, userEmail?: string) => {
    if (!window.confirm(`Send a password reset email to ${userEmail || 'this user'}?`)) return;
    setSendingResetFor(userId);
    try {
      const res = await api.post<{ message: string }>(`/api/users/${userId}/send-reset-email`);
      setMessage(res.data.message || 'Password reset email sent');
      setMessageType('success');
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Error sending reset email');
      setMessageType('error');
    } finally {
      setSendingResetFor(null);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/users', formData);
      setMessage('User created — an account setup email has been sent to them');
      setMessageType('success');
      setShowAddModal(false);
      setFormData({ email: '', username: '', name: '', role: 'RESIDENT' });
      fetchUsers();
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Error creating user');
      setMessageType('error');
    }
  };

  const handleOpenEditModal = (u: User) => {
    setEditingUser(u);
    setEditFormData({ name: u.name || '', email: u.email || '', username: u.username || '', contactNumber: u.contactNumber || '' });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    if (!selectedGroupId) return;
    try {
      const response = await api.put<User>(`/api/users/${editingUser.id}`, editFormData, {
        headers: { 'x-selected-group': selectedGroupId }
      });
      setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...response.data } : u));
      setShowEditModal(false);
      setEditingUser(null);
      setMessage('User updated');
      setMessageType('success');
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Error updating user');
      setMessageType('error');
    }
  };

  // ── Rent Schedule ────────────────────────────────────────────────────────────

  const openRentModal = async (u: User) => {
    setRentModalUser(u);
    setShowRentModal(true);
    setShowScheduleForm(false);
    setEditingScheduleId(null);
    setScheduleForm({ ...emptyForm, startDate: new Date().toISOString().slice(0, 10) });
    await loadSchedules(u.id);
  };

  const loadSchedules = async (userId: string) => {
    setSchedulesLoading(true);
    try {
      const res = await api.get<RentSchedule[]>(`/api/rent-schedules?userId=${userId}`, { headers: headers() });
      setSchedules(res.data);
    } catch {
      setMessage('Failed to load rent schedules');
      setMessageType('error');
    } finally {
      setSchedulesLoading(false);
    }
  };

  const openNewScheduleForm = () => {
    setEditingScheduleId(null);
    setScheduleForm({ ...emptyForm, startDate: new Date().toISOString().slice(0, 10) });
    setShowScheduleForm(true);
  };

  const openEditScheduleForm = (s: RentSchedule) => {
    setEditingScheduleId(s.id);
    setScheduleForm({
      amount: String(s.amount),
      frequency: s.frequency,
      startDate: s.startDate.slice(0, 10),
      duration: s.duration ? String(s.duration) : '',
      description: s.description || ''
    });
    setShowScheduleForm(true);
  };

  const handleSaveSchedule = async () => {
    if (!rentModalUser) return;
    if (!scheduleForm.amount || !scheduleForm.frequency || !scheduleForm.startDate) {
      setMessage('Amount, frequency, and start date are required');
      setMessageType('error');
      return;
    }
    try {
      const payload = {
        userId: rentModalUser.id,
        amount: parseFloat(scheduleForm.amount),
        frequency: scheduleForm.frequency,
        startDate: scheduleForm.startDate,
        duration: scheduleForm.duration ? parseInt(scheduleForm.duration) : null,
        description: scheduleForm.description || undefined
      };
      if (editingScheduleId) {
        await api.put(`/api/rent-schedules/${editingScheduleId}`, payload, { headers: headers() });
        setMessage('Schedule updated');
      } else {
        await api.post('/api/rent-schedules', payload, { headers: headers() });
        setMessage('Rent schedule created');
      }
      setMessageType('success');
      setShowScheduleForm(false);
      await loadSchedules(rentModalUser.id);
      await fetchUsers(); // sync rentAmount in the table
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to save schedule');
      setMessageType('error');
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    if (!window.confirm('Delete this rent schedule?')) return;
    if (!rentModalUser) return;
    try {
      await api.delete(`/api/rent-schedules/${id}`, { headers: headers() });
      setMessage('Schedule deleted');
      setMessageType('success');
      await loadSchedules(rentModalUser.id);
    } catch (e: any) {
      setMessage(e.response?.data?.message || 'Failed to delete');
      setMessageType('error');
    }
  };

  // ── Computed ─────────────────────────────────────────────────────────────────

  const previewEndDate = calcEndDate(
    scheduleForm.startDate,
    scheduleForm.frequency,
    parseInt(scheduleForm.duration) || 0
  );

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'RESIDENT': return 'bg-green-500/20 text-green-300 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const activeSchedule = (userId: string): RentSchedule | undefined =>
    schedules.find(s => s.userId === userId && s.isActive);

  if (user?.role !== 'ADMIN') {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Access denied. Only administrators can manage users.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center text-white/80">Loading users…</div>
    );
  }

  return (
    <div className="p-6">
      <div className="floating-card">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            Add New User
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded flex items-center justify-between ${
            messageType === 'success'
              ? 'bg-green-500/10 border border-green-500/30 text-green-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}>
            <span>{message}</span>
            <button onClick={() => setMessage('')} className="ml-2 text-lg font-bold hover:opacity-70">×</button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4 text-white/80 font-medium">Name</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Email</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Username</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Role</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Rent Schedule</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Joined</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((userItem) => (
                <tr key={userItem.id} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 text-white">{userItem.name || 'N/A'}</td>
                  <td className="py-3 px-4 text-white/80">{userItem.email}</td>
                  <td className="py-3 px-4 text-white/80">{userItem.username}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs border ${getRoleBadgeClass(userItem.role)}`}>
                      {userItem.role}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {userItem.role === 'RESIDENT' ? (
                      <button
                        onClick={() => openRentModal(userItem)}
                        className="flex items-center gap-2 group"
                        title="Configure rent schedule"
                      >
                        {userItem.rentAmount ? (
                          <div className="text-left">
                            <span className="text-green-400 font-semibold">
                              {formatCurrency(userItem.rentAmount)}
                            </span>
                            <span className="text-white/40 text-xs ml-1">/mo</span>
                            <div className="text-xs text-white/30 group-hover:text-[#22d3ee] transition-colors">
                              click to configure ›
                            </div>
                          </div>
                        ) : (
                          <span className="text-white/40 text-sm group-hover:text-[#22d3ee] transition-colors flex items-center gap-1">
                            <PlusIcon className="w-3 h-3" />
                            Set rent schedule
                          </span>
                        )}
                      </button>
                    ) : (
                      <span className="text-white/40">N/A</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-white/80">
                    {new Date(userItem.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {userItem.id === user?.id ? (
                        <button
                          onClick={() => navigate('/profile')}
                          className="text-[#22d3ee] hover:text-[#67e8f9] transition-colors text-sm font-medium"
                        >
                          My Profile →
                        </button>
                      ) : (
                        <button
                          onClick={() => handleOpenEditModal(userItem)}
                          className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                        >
                          Edit
                        </button>
                      )}
                      {userItem.id !== user.id && (
                        <button
                          onClick={() => handleSendResetEmail(userItem.id, userItem.email)}
                          disabled={sendingResetFor === userItem.id}
                          className="text-amber-400 hover:text-amber-300 transition-colors text-sm disabled:opacity-50"
                          title="Email this user a secure link to reset their password"
                        >
                          {sendingResetFor === userItem.id ? 'Sending…' : 'Reset Password'}
                        </button>
                      )}
                      {userItem.id !== user.id && (
                        <button
                          onClick={() => handleDeleteUser(userItem.id, userItem.name || userItem.username)}
                          className="text-red-400 hover:text-red-300 transition-colors text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="text-center py-8 text-white/60">No users found in this property.</div>
          )}
        </div>
      </div>

      {/* ══════════════════ RENT SCHEDULE MODAL ══════════════════ */}
      {showRentModal && rentModalUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#1a1a1b] border border-white/10 rounded-2xl w-full max-w-xl my-8">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">Rent Schedule</h2>
                <p className="text-sm text-white/50 mt-0.5">{rentModalUser.name} · {rentModalUser.email}</p>
              </div>
              <button
                onClick={() => { setShowRentModal(false); setShowScheduleForm(false); }}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-white/60" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Current active schedule summary */}
              {schedulesLoading ? (
                <div className="text-center py-4 text-white/40">Loading schedules…</div>
              ) : schedules.filter(s => s.isActive).length === 0 && !showScheduleForm ? (
                <div className="text-center py-6">
                  <CalendarDaysIcon className="w-10 h-10 text-white/20 mx-auto mb-2" />
                  <p className="text-white/50 text-sm mb-4">No active rent schedule yet.</p>
                  <button onClick={openNewScheduleForm} className="btn-primary">
                    Create Rent Schedule
                  </button>
                </div>
              ) : null}

              {/* Schedule list */}
              {!schedulesLoading && schedules.length > 0 && !showScheduleForm && (
                <div className="space-y-3">
                  {schedules.map(s => (
                    <div
                      key={s.id}
                      className={`rounded-xl p-4 border ${
                        s.isActive
                          ? 'bg-green-500/8 border-green-500/20'
                          : 'bg-white/3 border-white/10 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Amount + frequency */}
                          <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className={`text-xl font-bold ${s.isActive ? 'text-green-400' : 'text-white/60'}`}>
                              {formatCurrency(s.amount)}
                            </span>
                            <span className="text-sm text-white/40">{freqAbbr(s.frequency)}</span>
                            {s.isActive && (
                              <span className="text-xs bg-green-500/20 text-green-400 border border-green-500/30 px-1.5 py-0.5 rounded-full ml-1">
                                Active
                              </span>
                            )}
                          </div>

                          {/* Frequency label */}
                          <p className="text-sm text-white/50 mt-0.5">{freqLabel(s.frequency)}</p>

                          {/* Date range */}
                          <div className="flex items-center gap-2 mt-2 text-xs text-white/40">
                            <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>
                              Starts {formatDate(s.startDate)}
                              {s.endDate
                                ? ` → ends ${formatDate(s.endDate)}`
                                  + (s.duration ? ` (${s.duration} ${freqLabel(s.frequency).toLowerCase()} periods)` : '')
                                : ' · ongoing'
                              }
                            </span>
                          </div>

                          {/* Next charge */}
                          <p className="text-xs text-white/30 mt-1">
                            Next charge: {formatDate(s.nextRunDate)}
                          </p>

                          {s.description && (
                            <p className="text-xs text-white/30 mt-0.5 italic">{s.description}</p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => openEditScheduleForm(s)}
                            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4 text-white/40 hover:text-white" />
                          </button>
                          <button
                            onClick={() => handleDeleteSchedule(s.id)}
                            className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4 text-white/40 hover:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add another schedule button */}
                  <button
                    onClick={openNewScheduleForm}
                    className="w-full py-3 rounded-xl border border-dashed border-white/15 hover:border-[#22d3ee]/40 hover:bg-[#22d3ee]/5 transition-all text-sm text-white/35 hover:text-[#22d3ee] flex items-center justify-center gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add New Schedule
                  </button>
                </div>
              )}

              {/* Schedule form */}
              {showScheduleForm && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-white">
                    {editingScheduleId ? 'Edit Schedule' : 'New Rent Schedule'}
                  </h3>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={scheduleForm.amount}
                        onChange={e => setScheduleForm(f => ({ ...f, amount: e.target.value }))}
                        className="input-field pl-7"
                        placeholder="e.g. 1500"
                      />
                    </div>
                  </div>

                  {/* Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">Billing Frequency *</label>
                    <select
                      value={scheduleForm.frequency}
                      onChange={e => setScheduleForm(f => ({ ...f, frequency: e.target.value }))}
                      className="input-field"
                    >
                      {FREQUENCIES.map(freq => (
                        <option key={freq.value} value={freq.value}>{freq.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Start date */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">Start Charging On *</label>
                    <input
                      type="date"
                      value={scheduleForm.startDate}
                      onChange={e => setScheduleForm(f => ({ ...f, startDate: e.target.value }))}
                      className="input-field"
                    />
                    <p className="text-xs text-white/30 mt-1">First invoice will be generated on this date.</p>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Duration <span className="text-white/30">(optional — leave blank for ongoing)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={scheduleForm.duration}
                        onChange={e => setScheduleForm(f => ({ ...f, duration: e.target.value }))}
                        className="input-field w-28"
                        placeholder="e.g. 12"
                      />
                      <span className="text-sm text-white/50">
                        {scheduleForm.frequency === 'WEEKLY' && 'weeks'}
                        {scheduleForm.frequency === 'BI_WEEKLY' && 'bi-weekly periods'}
                        {scheduleForm.frequency === 'MONTHLY' && 'months'}
                        {scheduleForm.frequency === 'QUARTERLY' && 'quarters'}
                        {scheduleForm.frequency === 'YEARLY' && 'years'}
                      </span>
                    </div>

                    {/* Auto-calculated end date preview */}
                    {scheduleForm.duration && parseInt(scheduleForm.duration) > 0 && previewEndDate && (
                      <div className="mt-2 flex items-center gap-2 text-sm bg-[#22d3ee]/10 border border-[#22d3ee]/20 rounded-xl px-3 py-2">
                        <CalendarDaysIcon className="w-4 h-4 text-[#22d3ee] flex-shrink-0" />
                        <span className="text-white/70">
                          Lease ends on{' '}
                          <span className="text-white font-semibold">
                            {previewEndDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </span>
                        </span>
                      </div>
                    )}

                    {(!scheduleForm.duration || parseInt(scheduleForm.duration) <= 0) && (
                      <p className="text-xs text-white/30 mt-1">No end date — schedule continues until manually stopped or deleted.</p>
                    )}
                  </div>

                  {/* Description (optional) */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Description <span className="text-white/30">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={scheduleForm.description}
                      onChange={e => setScheduleForm(f => ({ ...f, description: e.target.value }))}
                      className="input-field"
                      placeholder="e.g. 12-month lease, Unit 4B"
                    />
                  </div>

                  {/* Form actions */}
                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowScheduleForm(false)}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveSchedule}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      <CheckIcon className="w-4 h-4" />
                      {editingScheduleId ? 'Save Changes' : 'Create Schedule'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ EDIT USER MODAL ══════════════════ */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="floating-card max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Edit User</h2>
              <button onClick={() => setShowEditModal(false)} className="text-white/60 hover:text-white">✕</button>
            </div>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Full Name</label>
                <input type="text" required value={editFormData.name}
                  onChange={e => setEditFormData(p => ({ ...p, name: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Email</label>
                <input type="email" required value={editFormData.email}
                  onChange={e => setEditFormData(p => ({ ...p, email: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Username</label>
                <input type="text" required value={editFormData.username}
                  onChange={e => setEditFormData(p => ({ ...p, username: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Phone Number</label>
                <input type="tel" value={editFormData.contactNumber}
                  onChange={e => setEditFormData(p => ({ ...p, contactNumber: e.target.value }))}
                  className="input-field" placeholder="e.g. (555) 123-4567" />
              </div>
              <p className="text-xs text-white/40">Role and password can be managed separately.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEditModal(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" className="btn-primary flex-1">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════ ADD USER MODAL ══════════════════ */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="floating-card max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Add New User</h2>
              <button onClick={() => setShowAddModal(false)} className="text-white/60 hover:text-white">✕</button>
            </div>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Email</label>
                <input type="email" required value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Username</label>
                <input type="text" required value={formData.username}
                  onChange={e => setFormData(p => ({ ...p, username: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Full Name</label>
                <input type="text" required value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Role</label>
                <select value={formData.role}
                  onChange={e => setFormData(p => ({ ...p, role: e.target.value as 'ADMIN' | 'RESIDENT' }))}
                  className="input-field">
                  <option value="RESIDENT">Resident</option>
                  <option value="ADMIN">Admin</option>
                </select>
                <p className="mt-2 text-sm text-white/60">Vendor accounts are managed on the Vendors page.</p>
              </div>
              <p className="text-xs text-white/50 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                The user will receive an email with a secure link to set their own password. You never see or choose it.
              </p>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" className="btn-primary flex-1">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
